import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { useSendingTypeScope, sendingTypeScopeKey, type SendingTypeScope } from './useSendingTypeScope';
import { queryClient } from '../lib/queryClient';

interface StatsParams {
  granularity: 'day' | 'hour' | 'minute';
  sendingTypeScope?: SendingTypeScope;
  requireComplete?: boolean;
  workspaceId?: string;
  startDate?: string;
  endDate?: string;
  requestVersion?: number;
  relativeRange?: {
    duration: number;
    unit: string;
  };
  enabled?: boolean;
}

/** Default Analytics landing query (Last 1 day, hourly). Keep in sync with AnalyticsDashboard. */
export const DEFAULT_ANALYTICS_PREFETCH = {
  granularity: 'hour' as const,
  relativeRange: { duration: 1, unit: 'Days' },
  requestVersion: 0,
};

export function getAnalyticsStaleTime(granularity: StatsParams['granularity']): number {
  if (granularity === 'minute') return 10 * 1000;
  if (granularity === 'hour') return 30 * 1000;
  return 60 * 1000;
}

export function buildAnalyticsQueryKey(params: StatsParams) {
  const { granularity, startDate, endDate, requestVersion, relativeRange } = params;
  return [
    'analytics',
    params.workspaceId,
    params.sendingTypeScope,
    params.requireComplete,
    granularity,
    startDate,
    endDate,
    relativeRange?.duration,
    relativeRange?.unit,
    requestVersion,
  ] as const;
}

function buildStatsQueryParams(params: StatsParams): Record<string, string> {
  const { granularity, startDate, endDate, relativeRange } = params;
  const queryParams: Record<string, string> = { granularity, ...(params.sendingTypeScope && { sendingTypeScope: params.sendingTypeScope }) };
  if (params.requireComplete) queryParams.requireComplete = 'true';
  if (relativeRange) {
    const unitMilliseconds = relativeRange.unit === 'Minutes'
      ? 60 * 1000
      : relativeRange.unit === 'Hours'
        ? 60 * 60 * 1000
        : 24 * 60 * 60 * 1000;
    const endTimestamp = Date.now();
    const startTimestamp = endTimestamp - relativeRange.duration * unitMilliseconds;
    queryParams.start_date = String(startTimestamp);
    queryParams.end_date = String(endTimestamp);
  } else {
    if (startDate) queryParams.start_date = startDate;
    if (endDate) queryParams.end_date = endDate;
  }
  return queryParams;
}

/**
 * Custom hook for fetching analytics stats with caching.
 * Cache duration varies based on granularity:
 * - Minute granularity: 10 seconds (very dynamic)
 * - Hour granularity: 30 seconds (more dynamic)
 * - Day granularity: 1 minute
 */
export function useAnalyticsStats(params: StatsParams) {
  const { granularity } = params;
  const { sendingTypeScope, workspaceId, isReady } = useSendingTypeScope();
  const scopedParams = { ...params, sendingTypeScope, workspaceId };

  return useQuery({
    queryKey: buildAnalyticsQueryKey(scopedParams),
    queryFn: async () => {
      const response = await apiService.getStats(buildStatsQueryParams(scopedParams));
      return response;
    },
    staleTime: getAnalyticsStaleTime(granularity),
    gcTime: 5 * 60 * 1000,
    enabled: (params.enabled ?? true) && isReady,
    // Keep prior stats visible while granularity/range refetch runs (avoids UI flash).
    placeholderData: (previousData, previousQuery) => previousQuery?.queryKey[1] === workspaceId && previousQuery.queryKey[2] === sendingTypeScope ? previousData : undefined,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

/** Warm the Analytics route chunk + default /stats query (e.g. nav hover). */
export function prefetchAnalyticsLanding() {
  void import('../pages/AnalyticsDashboard');
  const workspaceId = apiService.getWorkspaceId();
  const sendingTypeScope = queryClient.getQueryData<SendingTypeScope>(sendingTypeScopeKey(workspaceId || ''));
  if (!workspaceId || !sendingTypeScope) return;
  const params: StatsParams = {
    workspaceId,
    sendingTypeScope,
    granularity: DEFAULT_ANALYTICS_PREFETCH.granularity,
    relativeRange: DEFAULT_ANALYTICS_PREFETCH.relativeRange,
    requestVersion: DEFAULT_ANALYTICS_PREFETCH.requestVersion,
  };
  void queryClient.prefetchQuery({
    queryKey: buildAnalyticsQueryKey(params),
    queryFn: async () => apiService.getStats(buildStatsQueryParams(params)),
    staleTime: getAnalyticsStaleTime(params.granularity),
  });
}
